/* eslint-disable max-lines-per-function */
import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { combineLatest, of } from 'rxjs';
import { filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';
import { FeatureSpecSelectors } from '@core/feature-spec-data';
import { FeatureCode } from '@core/feature-spec-types';
import {
  jobIdScope,
  JobRequisitionsActions,
  JobRequisitionsSelectors,
  JobsFullActions,
  JobsFullSelectors,
  JobWorkflowAutomationsSelectors,
  OfferLanguagesActions,
  OfferLanguagesSelectors,
} from '@core/offers-data';
import type { OfferFull, OfferLanguagesSetup } from '@core/offers-types';
import { OfferStatus } from '@core/offers-types';
import type { OpenQuestionsTemplate } from '@core/open-questions-templates-types';
import {
  ReferralsOfferAssignmentActions,
  ReferralsOfferAssignmentSelectors,
} from '@core/referrals-data';
import type { ReferralOfferAssignment } from '@core/referrals-types';
import type { JobRequisition } from '@core/requisitions-types';
import { WorkflowAutomation } from '@core/workflow-automations-types';
import type { PublishEventProps, WorkModel } from '@recruitee/offers-legacy';
import { JobsAnalytics } from '@recruitee/offers-legacy';
import {
  getOfferScope,
  OpenQuestionsTemplatesOfferActions,
} from '@recruitee/open-questions-templates-data';
import { RequisitionsConfigSelectors } from '@recruitee/requisitions-settings-data';
import { WorkflowAutomationsAccessFacade } from '@recruitee/workflow-automations-logic';
import { PipelineTemplateStageAutomationsActions } from '@recruitee/workflow-templates-data';

type PublishPropsData = {
  newStatus: OfferStatus.Published | OfferStatus.Internal;
  openQuestionsTemplate: 'none' | 'custom' | 'template';
  job: OfferFull;
  languages: OfferLanguagesSetup | null;
  jobRequisitions: JobRequisition[] | null;
  referralsAssignment: ReferralOfferAssignment | null;
  pipelineAutomationsCount: number;
  jobAutomationsCount: number;
};

@Injectable()
export class JobsStatusAnalyticsHelperService {
  private readonly jobsAnalytics = inject(JobsAnalytics);
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly automationsAccessFacade = inject(WorkflowAutomationsAccessFacade);

  private hasReferralsFeature = toSignal(
    this.store
      .select(FeatureSpecSelectors.forCode(FeatureCode.Referrals))
      .pipe(map(config => config?.entitled)),
  );

  public trackJobStatusChange(
    jobId: number,
    newStatus: OfferStatus.Published | OfferStatus.Internal,
  ): void {
    this.loadJob(jobId)
      .pipe(
        tap(job => {
          if (this.hasReferralsFeature()) {
            this.store.dispatch(
              ReferralsOfferAssignmentActions.fetchOne({ payload: { id: job.id } }),
            );
          }

          this.store.dispatch(OfferLanguagesActions.fetchOne({ payload: { offerId: job.id } }));
        }),
        switchMap(job =>
          combineLatest([
            this.loadOpenQuestionsTemplate(job.id, job.openQuestionTemplateId),
            this.store.select(OfferLanguagesSelectors.byOfferId(job.id)).pipe(filterBoolean),
            this.getJobRequistionsData$(job.id),
            this.loadPipelineAutomations(job.pipelineTemplateId),
            this.getJobAutomations(job.id),
            ...(this.hasReferralsFeature()
              ? [this.store.select(ReferralsOfferAssignmentSelectors.byId(job.id))]
              : [of(null)]),
          ]).pipe(
            map(
              ([
                template,
                languages,
                jobRequisitions,
                pipelineAutomations,
                jobTemplateAutomations,
                referralsAssignment,
              ]) => ({
                template,
                languages,
                job,
                jobRequisitions,
                referralsAssignment,
                pipelineAutomations,
                jobTemplateAutomations,
              }),
            ),
          ),
        ),
        take(1),
      )
      .subscribe(
        ({
          job,
          template,
          languages,
          jobRequisitions,
          referralsAssignment,
          pipelineAutomations,
          jobTemplateAutomations,
        }) => {
          const openQuestionsTemplate = template
            ? template.custom
              ? 'custom'
              : 'template'
            : 'none';

          const props = this.getPublishProps({
            newStatus,
            openQuestionsTemplate,
            job,
            languages,
            jobRequisitions,
            referralsAssignment,
            pipelineAutomationsCount: pipelineAutomations?.length || 0,
            jobAutomationsCount: jobTemplateAutomations?.length || 0,
          });
          this.jobsAnalytics.trackPublish({ ...props });
        },
      );
  }

  public trackJobClose(jobId: number): void {
    this.loadJob(jobId).subscribe(job => {
      this.jobsAnalytics.trackClose({
        ...(job.numberOfOpenings
          ? { isAllOpeningsFilled: job.hiredCandidatesCount >= job.numberOfOpenings }
          : {}),
      });
    });
  }

  private getPublishProps({
    newStatus,
    job,
    languages,
    openQuestionsTemplate,
    jobRequisitions,
    referralsAssignment,
    pipelineAutomationsCount,
    jobAutomationsCount,
  }: PublishPropsData): Omit<PublishEventProps, 'source'> {
    return {
      state: newStatus === OfferStatus.Published ? 'public' : 'internal',
      languages: languages?.languages.filter(lang => lang.enabled).length || 1,
      screeningQuestions: openQuestionsTemplate,
      pipeline: job.pipelineTemplate!.custom ? 'custom' : 'template',
      profileFields: job.fieldset ? (job.fieldset.name ? 'template' : 'custom') : 'none',
      workModel: <WorkModel[]>[
        ...(job.onSite ? ['onSite'] : []),
        ...(job.remote ? ['remote'] : []),
        ...(job.hybrid ? ['hybrid'] : []),
      ],
      assignedLocationsCount: job.locationIds.length,
      isWorkLocationQuestionEnabled: job.visibilityOptions.includes('locations_question'),
      ...(jobRequisitions
        ? {
            isRequisitionAssigned: !!jobRequisitions.length,
            isConsistentWithRequisition: jobRequisitions.every(req => !req.dataMismatch),
          }
        : {}),
      isRequirementsFilled: !!job.requirements,
      isPrioritySet: !!job.priority,
      isOpeningEnabled: (!jobRequisitions || !jobRequisitions.length) && !!job.numberOfOpenings,
      isHighlightsFilled: !!job.highlightJson,
      applyWithEnabled: job.visibilityOptions.filter(option =>
        ['linkedin', 'xing', 'indeed'].includes(option),
      ),
      isSocialSharingDescriptionFilled: !!job.sharingDescription,
      isReferralsEnabled: job.enabledForReferrals,
      isReferralsRewardAssigned: job.enabledForReferrals && !!referralsAssignment?.referralRewardId,
      isAutoConfEmailEnabled: job.emailConfirmation,
      hasCustomFields: !!job.dynamicFields?.length,
      hasAutomations: !!pipelineAutomationsCount || !!jobAutomationsCount,
      pipelineAutomationsCount,
      jobAutomationsCount,
    };
  }

  private loadPipelineAutomations(templateId: number): Observable<WorkflowAutomation[]> {
    if (!this.automationsAccessFacade.hasJobEditorAutomations()) return of([]);

    this.store.dispatch(
      PipelineTemplateStageAutomationsActions.fetchStages({
        payload: { pipelineTemplateId: templateId },
      }),
    );

    return this.actions$.pipe(
      ofType(PipelineTemplateStageAutomationsActions.fetchStagesSuccess),
      filter(({ payload }) => payload.pipelineTemplateId === templateId),
      take(1),
      map(({ payload }) => payload.data.flatMap(stage => stage.workflowAutomations)),
    );
  }

  private getJobAutomations(jobId: number): Observable<WorkflowAutomation[] | null> {
    return of(jobId).pipe(
      switchMap(jobId => {
        if (!this.automationsAccessFacade.hasJobEditorAutomations()) return of([]);
        return this.store
          .select(
            JobWorkflowAutomationsSelectors.entities({
              scope: jobIdScope(jobId),
            }),
          )
          .pipe(filterBoolean);
      }),
    );
  }

  private loadJob(id: number): Observable<OfferFull> {
    return this.store.select(JobsFullSelectors.isLoaded(id)).pipe(
      tap(isLoaded => {
        if (!isLoaded) JobsFullActions.fetchOne({ payload: { id } });
      }),
      switchMap(() => {
        const isLoading$ = this.store.select(JobsFullSelectors.isPending());
        const data$ = this.store.select(JobsFullSelectors.byId(id));

        return data$.pipe(
          withLatestFrom(isLoading$),
          filter(([data, isLoading]) => !isLoading),
          map(([data, isLoading]) => data),
          filterBoolean,
          take(1),
        );
      }),
    );
  }

  private getJobRequistionsData$(jobId: number) {
    return this.store.select(RequisitionsConfigSelectors.requisitionsFeatureEnabled).pipe(
      switchMap(enabled => {
        if (!enabled) return of(null);
        this.store.dispatch(JobRequisitionsActions.fetchOne({ payload: { offerId: jobId } }));
        return this.store.select(JobRequisitionsSelectors.byOfferId(jobId)).pipe(filterBoolean);
      }),
      take(1),
    );
  }

  private loadOpenQuestionsTemplate(
    offerId: number,
    templateId: number | null,
  ): Observable<OpenQuestionsTemplate | null> {
    return templateId ? this.loadOpenQuestionsTemplateData(offerId, templateId) : of(null);
  }

  private loadOpenQuestionsTemplateData(
    offerId: number,
    templateId: number,
  ): Observable<OpenQuestionsTemplate> {
    const openQuestionsScope = getOfferScope(offerId);
    this.store.dispatch(
      OpenQuestionsTemplatesOfferActions.fetchOne({
        payload: {
          offerId,
          templateId,
          scope: openQuestionsScope,
        },
      }),
    );

    return this.actions$.pipe(ofType(OpenQuestionsTemplatesOfferActions.fetchOneSuccess)).pipe(
      map(({ payload }) => payload.data),
      filterBoolean,
      take(1),
    );
  }
}
